import { defineStore } from 'pinia';
import { ref, computed, toRaw } from 'vue';

import { useSocketStore, type HubSocket, type SocketHandler } from './socket';
import { useImageAssetsStore } from './imageAssets';
import { getRuntimeConfig } from '../config/runtime';
import { useWebRTCStore } from './webrtc';
import { apiFetch } from '../services/api';
import type { Device, DeviceValueWithReferences, Room, Area, Scene, Integration } from '../types';
import {
  deserializeDevice,
  deserializeRoom,
  deserializeArea,
  deserializeScene,
  deserializeIntegration,
} from '../utils/serializers';
import { sortDevicesByHierarchy, sortRoomsByHierarchy, sortAreas } from '../utils/sorting';
import { wireJsonValue } from '../utils/wire';

// Re-export types for backward compatibility
export type { Device, Room, Area, Scene, Integration } from '../types';

/**
 * An entity row as the hub pushes it on the created/updated events: the row's
 * own JSON (the `deserialize*` helpers narrow it) plus the id every handler logs
 * and looks its subject up by.
 */
interface EntityRowPayload {
  id: string;
  [key: string]: unknown;
}

/**
 * A `device:updated` push: either the whole device row or — for a small update
 * like a thumbnail change — just `id` plus the fields that changed.
 */
interface DeviceUpdatePayload {
  id: string;
  thumbnailUrl?: string | null;
  stateThumbnailUrl?: string | null;
  /** A delta for the settings bag (the state image's S3 key rides here). */
  settings?: Record<string, unknown> | null;
  [key: string]: unknown;
}

/** The state image key a device's settings bag carries (`settings` is untyped). */
function stateThumbnailKey(settings: unknown): string | undefined {
  if (!settings || typeof settings !== 'object') return undefined;
  const key = (settings as Record<string, unknown>).stateThumbnailS3Key;
  return typeof key === 'string' && key.length > 0 ? key : undefined;
}

export const useEntitiesStore = defineStore('entities', () => {
  const socketStore = useSocketStore();

  // State
  const devices = ref<Device[]>([]);
  const rooms = ref<Room[]>([]);
  const areas = ref<Area[]>([]);
  const scenes = ref<Scene[]>([]);
  const integrations = ref<Integration[]>([]);
  const imageAssetsStore = useImageAssetsStore();

  const loading = ref(false);
  const loaded = ref(false);
  const error = ref<string | null>(null);
  const lastUpdated = ref<number>(0);

  function primeDeviceThumbnailCache(deviceList: Device[]): void {
    const config = getRuntimeConfig();
    const webrtcStore = useWebRTCStore();

    // In Electron/cloud mode, eager warming can flood the capped WebRTC request
    // queue during app startup. Device widgets lazy-fetch default and state
    // thumbnails on demand, so keep this warm-up for local/direct mode only.
    if (config.mode === 'cloud' || webrtcStore.connected) {
      return;
    }

    deviceList.forEach((device) => {
      // Default / fallback thumbnail
      const defaultKey = device.thumbnailS3Key;
      if (defaultKey) {
        const cached = imageAssetsStore.getImageUrlByS3Key(defaultKey);
        const p = cached ? Promise.resolve(cached) : imageAssetsStore.fetchImageUrlByS3Key(defaultKey);
        // Write the blob:// URL back onto the reactive device object so that
        // every widget template that reads device.thumbnailUrl directly will
        // automatically re-render once the fetch completes.
        void p.then((blobUrl) => {
          if (blobUrl) device.thumbnailUrl = blobUrl;
        });
      }

      // The state image a binary sensor shows while active
      const stateKey = stateThumbnailKey(device.settings);
      if (stateKey) {
        const cached = imageAssetsStore.getImageUrlByS3Key(stateKey);
        const p = cached ? Promise.resolve(cached) : imageAssetsStore.fetchImageUrlByS3Key(stateKey);
        void p.then((blobUrl) => {
          if (blobUrl) device.stateThumbnailUrl = blobUrl;
        });
      }
    });
  }

  // Computed getters
  const devicesByRoom = computed(() => {
    const map = new Map<string, Device[]>();
    devices.value.forEach((device) => {
      const roomId = device.roomId || 'unassigned';
      if (!map.has(roomId)) {
        map.set(roomId, []);
      }
      map.get(roomId)!.push(device);
    });
    return map;
  });

  const roomsByArea = computed(() => {
    const map = new Map<string, Room[]>();
    rooms.value.forEach((room) => {
      const areaId = room.areaId || 'unassigned';
      if (!map.has(areaId)) {
        map.set(areaId, []);
      }
      map.get(areaId)!.push(room);
    });
    return map;
  });

  const scenesByRoom = computed(() => {
    const map = new Map<string, Scene[]>();
    scenes.value.forEach((scene) => {
      const roomId = scene.roomId || 'unassigned';
      if (!map.has(roomId)) {
        map.set(roomId, []);
      }
      map.get(roomId)!.push(scene);
    });
    return map;
  });

  // Sorted entities by hierarchy
  const sortedDevices = computed(() => {
    return sortDevicesByHierarchy(devices.value, rooms.value, areas.value);
  });

  const sortedRooms = computed(() => {
    return sortRoomsByHierarchy(rooms.value, areas.value);
  });

  const sortedAreas = computed(() => {
    return sortAreas(areas.value);
  });

  // Helper functions for finding entities
  const getDevice = (deviceId: string) => devices.value?.find((d) => d.id === deviceId);
  const getRoom = (roomId: string) => rooms.value?.find((r) => r.id === roomId);
  const getArea = (areaId: string) => areas.value?.find((a) => a.id === areaId);
  const getScene = (sceneId: string) => scenes.value?.find((s) => s.id === sceneId);
  const getIntegration = (integrationId: string) => integrations.value?.find((i) => i.id === integrationId);

  // Promise cached while initialization is in-flight so concurrent callers await the same fetch
  let _initPromise: Promise<void> | null = null;

  /**
   * Initialize entities store - load all data
   */
  async function initialize() {
    if (loaded.value) return;
    if (_initPromise) return _initPromise;

    // Skip initialization in cloud mode until connected to hub
    const config = getRuntimeConfig();
    if (config.mode === 'cloud') {
      const webrtcStore = useWebRTCStore();
      if (!webrtcStore.connected) {
        console.log('[EntitiesStore] Skipping initialization in cloud mode (not connected to hub)');
        return;
      }
    }

    _initPromise = (async () => {
      loading.value = true;
      error.value = null;

      try {
        const [devicesData, roomsData, areasData, scenesData, integrationsData] = await Promise.all([
          apiFetch('/devices'),
          apiFetch('/rooms'),
          apiFetch('/areas'),
          apiFetch('/scenes'),
          apiFetch('/integrations'),
        ]);

        // Defensive: ensure responses are arrays (hub may return non-array on error)
        if (
          !Array.isArray(devicesData) ||
          !Array.isArray(roomsData) ||
          !Array.isArray(areasData) ||
          !Array.isArray(scenesData) ||
          !Array.isArray(integrationsData)
        ) {
          const bad = [
            !Array.isArray(devicesData) && '/devices',
            !Array.isArray(roomsData) && '/rooms',
            !Array.isArray(areasData) && '/areas',
            !Array.isArray(scenesData) && '/scenes',
            !Array.isArray(integrationsData) && '/integrations',
          ].filter(Boolean);
          console.error('[EntitiesStore] Non-array API responses', { endpoints: bad });
          throw new Error('API returned non-array for: ' + bad.join(', ') + '. Hub may be unreachable.');
        }

        devices.value = devicesData.map(deserializeDevice);
        rooms.value = roomsData.map(deserializeRoom);
        areas.value = areasData.map(deserializeArea);
        scenes.value = scenesData.map(deserializeScene);
        integrations.value = integrationsData.map(deserializeIntegration);

        primeDeviceThumbnailCache(devices.value);

        loaded.value = true;
        lastUpdated.value = Date.now();

        // Setup WebSocket listeners
        setupEntityListeners();

        console.log('[EntitiesStore] Initialized successfully');
      } catch (__caught: unknown) {
        const err = __caught as {
          response?: {
            status?: number;
            data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
          };
        };
        error.value = err.response?.data?.error || 'Failed to load entities';
        console.error('[EntitiesStore] Failed to initialize:', err);
        throw err;
      } finally {
        loading.value = false;
        _initPromise = null;
      }
    })();
    return _initPromise;
  }

  /**
   * Refresh all entities data
   */
  async function refresh() {
    console.log('[EntitiesStore] Refreshing entities...');
    loading.value = true;
    error.value = null;

    try {
      const [devicesData, roomsData, areasData, scenesData, integrationsData] = await Promise.all([
        apiFetch('/devices'),
        apiFetch('/rooms'),
        apiFetch('/areas'),
        apiFetch('/scenes'),
        apiFetch('/integrations'),
      ]);

      if (
        !Array.isArray(devicesData) ||
        !Array.isArray(roomsData) ||
        !Array.isArray(areasData) ||
        !Array.isArray(scenesData) ||
        !Array.isArray(integrationsData)
      ) {
        const bad = [
          !Array.isArray(devicesData) && '/devices',
          !Array.isArray(roomsData) && '/rooms',
          !Array.isArray(areasData) && '/areas',
          !Array.isArray(scenesData) && '/scenes',
          !Array.isArray(integrationsData) && '/integrations',
        ].filter(Boolean);
        console.error('[EntitiesStore] Non-array API responses (refresh)', { endpoints: bad });
        throw new Error('API returned non-array for: ' + bad.join(', ') + '. Hub may be unreachable.');
      }

      devices.value = devicesData.map(deserializeDevice);
      rooms.value = roomsData.map(deserializeRoom);
      areas.value = areasData.map(deserializeArea);
      scenes.value = scenesData.map(deserializeScene);
      integrations.value = integrationsData.map(deserializeIntegration);

      primeDeviceThumbnailCache(devices.value);

      lastUpdated.value = Date.now();

      console.log('[EntitiesStore] Refresh complete');
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to refresh entities';
      console.error('[EntitiesStore] Failed to refresh:', err);
    } finally {
      loading.value = false;
    }
  }

  async function loadIntegrations() {
    loading.value = true;
    error.value = null;

    try {
      const integrationsData = await apiFetch('/integrations');
      if (!Array.isArray(integrationsData)) {
        console.error('[EntitiesStore] /integrations returned non-array', typeof integrationsData);
        throw new Error('API returned non-array for /integrations. Hub may be unreachable.');
      }
      integrations.value = integrationsData.map(deserializeIntegration);
      lastUpdated.value = Date.now();
    } catch (__caught: unknown) {
      const err = __caught as {
        response?: {
          status?: number;
          data?: { error?: string; message?: string; details?: string; [k: string]: unknown };
        };
      };
      error.value = err.response?.data?.error || 'Failed to load integrations';
      console.error('[EntitiesStore] Failed to load integrations:', err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function resetForHubSwitch() {
    devices.value = [];
    rooms.value = [];
    areas.value = [];
    scenes.value = [];
    integrations.value = [];
    loading.value = false;
    loaded.value = false;
    error.value = null;
    lastUpdated.value = 0;
    _initPromise = null;
  }

  /**
   * Check if data is stale (older than 30 seconds)
   */
  function isStale(): boolean {
    if (!lastUpdated.value) return true;
    const age = Date.now() - lastUpdated.value;
    return age > 30000; // 30 seconds
  }

  // ===== Optimistic update helpers =====

  /**
   * Add a row the socket pushed, replacing it when the id is already present.
   * Pushes can legitimately arrive for a row we already hold (the hub also
   * answers the request that created it), and a plain `push` would leave two
   * entries with the same id — which is then indistinguishable in the UI and
   * deletes as one.
   */
  function upsertById<T extends { id: string }>(list: T[], item: T): void {
    const index = list.findIndex((existing) => existing.id === item.id);
    if (index === -1) {
      list.push(item);
    } else {
      list[index] = { ...list[index], ...item };
    }
  }

  function addDevice(device: Device) {
    upsertById(devices.value, device);
  }

  /**
   * A device's value rows, falling back to the legacy `values` alias older hub
   * payloads carried the same rows under.
   */
  function getDeviceValueRows(device: Device): DeviceValueWithReferences[] | undefined {
    if (Array.isArray(device.deviceValues)) return device.deviceValues;
    const legacy: unknown = 'values' in device ? device.values : undefined;
    return Array.isArray(legacy) ? (legacy as DeviceValueWithReferences[]) : undefined;
  }

  function updateDevice(id: string, updates: Partial<Device>) {
    const index = devices.value.findIndex((d) => d.id === id);
    if (index !== -1) {
      devices.value[index] = { ...devices.value[index], ...updates };
    }
  }

  function removeDevice(id: string) {
    devices.value = devices.value.filter((d) => d.id !== id);
  }

  function addRoom(room: Room) {
    upsertById(rooms.value, room);
  }

  function updateRoom(id: string, updates: Partial<Room>) {
    const index = rooms.value.findIndex((r) => r.id === id);
    if (index !== -1) {
      rooms.value[index] = { ...rooms.value[index], ...updates };
    }
  }

  function removeRoom(id: string) {
    rooms.value = rooms.value.filter((r) => r.id !== id);
  }

  function addArea(area: Area) {
    upsertById(areas.value, area);
  }

  function updateArea(areaId: string, updates: Partial<Area>) {
    const index = areas.value.findIndex((a) => a.id === areaId);
    if (index !== -1) {
      areas.value[index] = { ...areas.value[index], ...updates };
    }
  }

  function removeArea(areaId: string) {
    areas.value = areas.value.filter((a) => a.id !== areaId);
  }

  function addScene(scene: Scene) {
    upsertById(scenes.value, scene);
  }

  function updateScene(sceneId: string, updates: Partial<Scene>) {
    const index = scenes.value.findIndex((s) => s.id === sceneId);
    if (index !== -1) {
      scenes.value[index] = { ...scenes.value[index], ...updates };
    }
  }

  function removeScene(sceneId: string) {
    scenes.value = scenes.value.filter((s) => s.id !== sceneId);
  }

  function addIntegration(integration: Integration) {
    const index = integrations.value.findIndex((i) => i.id === integration.id);
    if (index === -1) {
      integrations.value.push(integration);
    } else {
      integrations.value[index] = { ...integrations.value[index], ...integration };
    }
  }

  function updateIntegration(id: string, updates: Partial<Integration>) {
    const index = integrations.value.findIndex((i) => i.id === id);
    if (index !== -1) {
      integrations.value[index] = { ...integrations.value[index], ...updates };
    }
  }

  function removeIntegration(id: string) {
    integrations.value = integrations.value.filter((i) => i.id !== id);
  }

  // ===== WebSocket event listeners =====

  /**
   * The socket handle the entity listeners are currently bound to, plus the
   * exact (event, handler) pairs registered on it.
   *
   * Binding must be idempotent: `initialize()` runs again after a hub switch or
   * a transport swap, while `connect()` deliberately reuses an existing socket
   * when the target is unchanged — and the WebRTC status bridge is a singleton
   * for its whole life. Registering a second set of handlers on the same handle
   * delivers every push twice: `room:created` then adds the row twice, and
   * deleting either copy (same id) removes both until a refresh. The pairs are
   * kept so that binding to a *different* handle first unbinds the previous one.
   */
  let _listenerSocket: HubSocket | null = null;
  let _listenerHandlers: Array<{ event: string; handler: SocketHandler }> = [];

  function setupEntityListeners() {
    const socket = socketStore.socket;
    if (!socket) {
      console.warn('[EntitiesStore] Socket not available, skipping entity listeners setup');
      return;
    }

    // Same handle as last time — it is already bound. Compared raw because
    // reading the socket back out of the store hands out Vue's reactive proxy.
    if (_listenerSocket && toRaw(_listenerSocket) === toRaw(socket)) {
      return;
    }

    // A different handle (hub switch, transport swap, reconnect): unhook the old
    // pairs first so a previous socket or bridge cannot keep feeding this store.
    if (_listenerSocket) {
      for (const { event, handler } of _listenerHandlers) {
        _listenerSocket.off(event, handler);
      }
    }
    _listenerHandlers = [];
    _listenerSocket = socket;

    const register = <T>(event: string, handler: (data: T) => void): void => {
      const bound = handler as SocketHandler;
      socket.on(event, bound);
      _listenerHandlers.push({ event, handler: bound });
    };

    console.log('[EntitiesStore] Setting up entity WebSocket listeners');

    // Device value change events - keep deviceValues in sync so
    // components that mount later don't show stale data.
    // Batched: socket events arrive in separate macrotasks (each triggers its own
    // Vue render cycle). We collect them over a 50 ms window and apply all at once
    // so Vue batches the DOM updates into a single render, reducing CPU/GPU load
    // on slow devices like Android tablets.
    const _pendingValueUpdates: Array<{ deviceId: string; valueKey: string; value: unknown }> = [];
    let _valueFlushTimer: ReturnType<typeof setTimeout> | null = null;

    function _flushValueUpdates() {
      _valueFlushTimer = null;
      for (const item of _pendingValueUpdates.splice(0)) {
        const device = devices.value.find((d) => d.id === item.deviceId);
        if (!device?.deviceValues) continue;
        const dv = device.deviceValues.find((v) => v.valueKey === item.valueKey);
        if (dv) dv.currentValue = wireJsonValue(item.value);
      }
    }

    register('device_value_changed', (data: { deviceId?: string; valueKey?: string; value?: unknown }) => {
      if (!data?.deviceId || !data?.valueKey) return;
      _pendingValueUpdates.push({ deviceId: data.deviceId, valueKey: data.valueKey, value: data.value });
      if (!_valueFlushTimer) {
        _valueFlushTimer = setTimeout(_flushValueUpdates, 50);
      }
    });

    // Device events
    register('device:created', (data: EntityRowPayload) => {
      console.log('[EntitiesStore] Device created:', data.id);
      addDevice(deserializeDevice(data));
      lastUpdated.value = Date.now();
    });

    register('device:updated', (data: DeviceUpdatePayload) => {
      console.log('[EntitiesStore] Device updated:', data.id);
      // If the payload only carries a small subset of fields (e.g. thumbnail update),
      // apply it as a raw partial merge rather than a full deserialize to avoid
      // clobbering existing fields with undefined.
      const existing = devices.value.find((d) => d.id === data.id);
      if (existing && Object.keys(data).length <= 4) {
        // Partial update – merge only the keys that are explicitly present
        const partial: Partial<Device> = {};
        if (data.thumbnailUrl !== undefined) partial.thumbnailUrl = data.thumbnailUrl;
        if (data.stateThumbnailUrl !== undefined) partial.stateThumbnailUrl = data.stateThumbnailUrl;
        if (data.settings !== undefined && data.settings !== null) {
          partial.settings = {
            ...((existing.settings as Record<string, unknown> | undefined) ?? {}),
            ...data.settings,
          } as Device['settings'];
        }
        updateDevice(data.id, partial);
      } else {
        updateDevice(data.id, deserializeDevice(data));
      }
      lastUpdated.value = Date.now();
    });

    register('device:deleted', (data: { id: string }) => {
      console.log('[EntitiesStore] Device deleted:', data.id);
      removeDevice(data.id);
      lastUpdated.value = Date.now();
    });

    // Room events
    register('room:created', (data: EntityRowPayload) => {
      console.log('[EntitiesStore] Room created:', data.id);
      addRoom(deserializeRoom(data));
      lastUpdated.value = Date.now();
    });

    register('room:updated', (data: EntityRowPayload) => {
      console.log('[EntitiesStore] Room updated:', data.id);
      updateRoom(data.id, deserializeRoom(data));
      lastUpdated.value = Date.now();
    });

    register('room:deleted', (data: { id: string }) => {
      console.log('[EntitiesStore] Room deleted:', data.id);
      removeRoom(data.id);
      lastUpdated.value = Date.now();
    });

    // Area events
    register('area:created', (data: EntityRowPayload) => {
      console.log('[EntitiesStore] Area created:', data.id);
      addArea(deserializeArea(data));
      lastUpdated.value = Date.now();
    });

    register('area:updated', (data: EntityRowPayload) => {
      console.log('[EntitiesStore] Area updated:', data.id);
      updateArea(data.id, deserializeArea(data));
      lastUpdated.value = Date.now();
    });

    register('area:deleted', (data: { id: string }) => {
      console.log('[EntitiesStore] Area deleted:', data.id);
      removeArea(data.id);
      lastUpdated.value = Date.now();
    });

    // Scene events
    register('scene:created', (data: EntityRowPayload) => {
      console.log('[EntitiesStore] Scene created:', data.id);
      addScene(deserializeScene(data));
      lastUpdated.value = Date.now();
    });

    register('scene:updated', (data: EntityRowPayload) => {
      console.log('[EntitiesStore] Scene updated:', data.id);
      updateScene(data.id, deserializeScene(data));
      lastUpdated.value = Date.now();
    });

    register('scene:deleted', (data: { id: string }) => {
      console.log('[EntitiesStore] Scene deleted:', data.id);
      removeScene(data.id);
      lastUpdated.value = Date.now();
    });

    // Integration events
    register('integration:created', (data: EntityRowPayload) => {
      console.log('[EntitiesStore] Integration created:', data.id);
      addIntegration(deserializeIntegration(data));
      lastUpdated.value = Date.now();
    });

    register('integration:updated', (data: EntityRowPayload) => {
      console.log('[EntitiesStore] Integration updated:', data.id);
      updateIntegration(data.id, deserializeIntegration(data));
      lastUpdated.value = Date.now();
    });

    register('integration:deleted', (data: { id?: string; integrationId?: string }) => {
      const integrationId = data.id ?? data.integrationId;
      if (!integrationId) return;

      console.log('[EntitiesStore] Integration deleted:', integrationId);
      removeIntegration(integrationId);
      lastUpdated.value = Date.now();
    });
  }

  return {
    // State
    devices,
    rooms,
    areas,
    scenes,
    integrations,
    loading,
    loaded,
    error,
    lastUpdated,

    // Computed
    devicesByRoom,
    roomsByArea,
    scenesByRoom,
    sortedDevices,
    sortedRooms,
    sortedAreas,

    // Getters
    getDevice,
    getRoom,
    getArea,
    getScene,
    getIntegration,

    // Actions
    initialize,
    refresh,
    resetForHubSwitch,
    loadIntegrations,
    isStale,

    // Optimistic updates (used internally and by components)
    addDevice,
    updateDevice,
    getDeviceValueRows,
    removeDevice,
    addRoom,
    updateRoom,
    removeRoom,
    addArea,
    updateArea,
    removeArea,
    addScene,
    updateScene,
    removeScene,
    addIntegration,
    updateIntegration,
    removeIntegration,
  };
});
